import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import Divider from '../../../../components/atoms/Divider'
import PageHeading from '../common/PageHeading'
import PageLayout from '../common/PageLayout'
import Loading from '../../../../components/molecules/Loading'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import FlexRow from '../../../../components/molecules/FlexRow'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import NoResults from '../common/NoResults'
import SydButton from '../../../../components/commonDesign/Button'
import SydInput from '../../../../components/commonDesign/SydInput'
import ClientFocusSelector from '../common/ClientFocusSelector'
import { useDenaliActions } from '../../../../components/organisms/DenaliActionPipeline/provider/DenaliActionProvider'
import { useListMeetings } from '../../../../api/meetings'
import MeetingCard from './MeetingCard'

const TERMINAL = ['completed', 'failed', 'cancelled']

const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'all', label: 'All' }
]

function getPresetDates (presetId) {
  const now = dayjs()
  switch (presetId) {
    case 'today':
      return { startDate: now.startOf('day').toISOString(), endDate: now.endOf('day').toISOString() }
    case 'week':
      return { startDate: now.startOf('week').toISOString(), endDate: now.endOf('week').toISOString() }
    case 'month':
      return { startDate: now.startOf('month').toISOString(), endDate: now.endOf('month').toISOString() }
    default:
      return { startDate: null, endDate: null }
  }
}

const useStyles = makeStyles(() => ({
  filterBar: {
    display: 'flex',
    borderRadius: 'var(--border-radius-md)',
    border: 'var(--border-neutral-thin)',
    overflow: 'hidden'
  },
  filterOption: {
    flex: 1,
    padding: '6px 12px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    backgroundColor: 'transparent',
    color: 'var(--dai-color-text-dim)',
    border: 'none',
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' }
  },
  filterOptionActive: {
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    fontWeight: 600,
    '&:hover': { backgroundColor: 'var(--color-primary-100)' }
  },
  dateRange: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    '& input': {
      fontSize: 'var(--font-size-xs)'
    }
  }
}))

function MeetingsHome () {
  const classes = useStyles()
  const [clientFocus, setClientFocus] = useState(null)
  const [datePreset, setDatePreset] = useState('all')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const { open } = useDenaliActions()

  const handlePresetClick = useCallback((presetId) => {
    setDatePreset(presetId)
    setCustomStart('')
    setCustomEnd('')
  }, [])

  const handleCustomDateChange = useCallback((field, value) => {
    setDatePreset('custom')
    if (field === 'start') setCustomStart(value)
    else setCustomEnd(value)
  }, [])

  const filters = useMemo(() => {
    let startDate = null
    let endDate = null

    if (datePreset === 'custom') {
      startDate = customStart ? dayjs(customStart).startOf('day').toISOString() : null
      endDate = customEnd ? dayjs(customEnd).endOf('day').toISOString() : null
    } else if (datePreset !== 'all') {
      const presetDates = getPresetDates(datePreset)
      startDate = presetDates.startDate
      endDate = presetDates.endDate
    }

    return {
      clientId: clientFocus?.subjectIdentifier ?? null,
      startDate,
      endDate
    }
  }, [clientFocus, datePreset, customStart, customEnd])

  const { data: meetings, isLoading } = useListMeetings(filters, {
    refetchInterval: (data) => {
      if (!Array.isArray(data)) return false
      return data.some(m => !TERMINAL.includes(m.status)) ? 15000 : false
    }
  })

  const meetingList = useMemo(() => Array.isArray(meetings) ? meetings : [], [meetings])

  const handleNewMeeting = useCallback(() => {
    const vars = clientFocus?.subjectIdentifier
      ? { clientId: clientFocus.subjectIdentifier }
      : {}
    open('denali-create-meeting', vars, vars)
  }, [open, clientFocus])

  if (isLoading) {
    return (
      <PageLayout variant='constrained'>
        <Loading />
      </PageLayout>
    )
  }

  return (
    <PageLayout variant='constrained'>
      <PageHeading title='Meetings'>
        <FlexRow className='gap-sm'>
          <ClientFocusSelector value={clientFocus} onChange={setClientFocus} />
          <SydButton
            variant='primary'
            size='sm'
            onClick={handleNewMeeting}
          >
            New Meeting
          </SydButton>
        </FlexRow>
      </PageHeading>
      <Divider />
      <FlexColumn className='gap-md' style={{ padding: 'var(--padding-md) 0' }}>
        <FlexRow className='gap-md items-center' style={{ flexWrap: 'wrap' }}>
          <div className={classes.filterBar}>
            {DATE_PRESETS.map(preset => (
              <button
                key={preset.id}
                type='button'
                className={`${classes.filterOption} ${datePreset === preset.id ? classes.filterOptionActive : ''}`}
                onClick={() => handlePresetClick(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className={classes.dateRange}>
            <SydInput
              type='date'
              placeholder='Start date'
              value={customStart}
              onChange={(e) => handleCustomDateChange('start', e?.target?.value ?? e)}
              size='sm'
            />
            <span className='font-size-xs' style={{ color: 'var(--dai-color-text-dim)' }}>to</span>
            <SydInput
              type='date'
              placeholder='End date'
              value={customEnd}
              onChange={(e) => handleCustomDateChange('end', e?.target?.value ?? e)}
              size='sm'
            />
          </div>
        </FlexRow>
      </FlexColumn>
      {meetingList.length === 0 ? (
        <NoResults message={clientFocus ? 'No meetings for this client' : 'No meetings found'} />
      ) : (
        <FadeIn>
          <FlexColumn className='gap-sm'>
            {meetingList.map(meeting => (
              <MeetingCard key={meeting.meetingId} meeting={meeting} />
            ))}
          </FlexColumn>
        </FadeIn>
      )}
    </PageLayout>
  )
}

export default MeetingsHome
